import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  Config,
  DashboardEndpointDef,
} from '~/api/def/endpoints/dashboardEndpoint';
import { useDashboards, useCreateDashboard } from '~/hooks/useDashboard';

type Dashboard =
  DashboardEndpointDef['methods']['GET_ALL']['response']['data'][number] & {
    metadata?: { type?: string; [key: string]: unknown };
    type?: string;
  };

export interface EnsureDashboardParams {
  type: string;
  name: string;
  initialConfig: Config[];
  portfolioId?: string | null;
}

export const useEnsureDashboard = ({
  type,
  name,
  initialConfig,
  portfolioId,
}: EnsureDashboardParams) => {
  const [dashboardId, setDashboardId] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const hasTriedRef = useRef(false);

  const { data: dashboardsResponse, isLoading: dashboardsLoading } =
    useDashboards();
  const createDashboard = useCreateDashboard();

  const dashboards = useMemo<Dashboard[]>(() => {
    return Array.isArray(dashboardsResponse)
      ? (dashboardsResponse as Dashboard[])
      : [];
  }, [dashboardsResponse]);

  useEffect(() => {
    if (dashboardsLoading) return;
    if (dashboardId) return;
    if (!portfolioId) return;
    if (createDashboard.isPending) return;
    if (hasTriedRef.current) return;

    const existing = dashboards.find(
      (d) => d.type === type || d.metadata?.type === type,
    );
    if (existing) {
      setDashboardId(existing.id);
      return;
    }

    hasTriedRef.current = true;
    const payload = {
      name,
      config: initialConfig,
      portfolioId: portfolioId || undefined,
      isDefault: false,
      type,
    };

    createDashboard.mutate(payload, {
      onSuccess: (response) => {
        if (response?.id) setDashboardId(response.id);
        setError(null);
      },
      onError: () => {
        setError(new Error('Failed to create dashboard'));
      },
    });
  }, [
    dashboardsLoading,
    dashboards,
    dashboardId,
    portfolioId,
    type,
    name,
    initialConfig,
    createDashboard,
  ]);

  return {
    dashboardId,
    isLoading:
      dashboardsLoading ||
      (!dashboardId &&
        (createDashboard as unknown as { isPending?: boolean }).isPending ===
          true),
    error,
  };
};
